import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Pagination from "react-js-pagination";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import { Box, Tooltip } from "@mui/material";
import { IoSearch } from "react-icons/io5";
import NotFound from "../../components/not-found/NotFound";
import IconButton from "../../components/Button/IconButton";
import { SecondaryButton } from "../../components/Button/Button";
import { CustomTabs, CustomTab } from "../../components/tabs/Tabs";
import ModalBox from "../../components/modal/ModalBox";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import useDebounce from "../../hooks/useDebounce";
import findSerialNumber from "../../utils/findSerialNumber";
import { CapitalizeFirstWord } from "../../utils/CapitalizeFirstWord";
import { toastMessage } from "../../utils/toastMessage";
import profileImage from "../../Assests/image/profile_not_found.png";
import common from "../../routes/common";
import { consultant, user as userRoutes } from "../../routes/route";
import {
  autoAssignTop20Api,
  fetchOpsAudienceApi,
  fetchOpsConsultantsApi,
  fetchOpsCustomersApi,
  fetchOpsOffersApi,
  fetchOpsSummaryApi,
  fetchOpsWhatsappLogsApi,
  saveOpsSettingsApi,
  sendOpsWhatsappApi,
  setOpsConsultantTierApi,
} from "../../services/operationsEcosystem";
import { getOpsEcosystemTabAccess } from "../../utils/operationsEcosystemAccess";

const { NOT_FOUND } = common;
const { USER_DETAIL } = userRoutes;

const TABS = [
  { id: "desk1", label: "Desk 1 - Priority Consultants" },
  { id: "desk2", label: "Desk 2 - Power Customers" },
  { id: "desk3", label: "Desk 3 - Core Operations" },
  { id: "offers", label: "WhatsApp Offers" },
];

const TIER_OPTIONS = [
  { value: "", label: "None" },
  { value: "platinum", label: "Platinum" },
  { value: "gold", label: "Gold" },
  { value: "top20", label: "Top 20" },
];

const instantTooltip = { enterDelay: 0, enterNextDelay: 0, leaveDelay: 0 };

const formatMoney = (value) => `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const formatDate = (value) => {
  if (!value) return "---";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "---";
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const dateInIst = (offsetDays = 0) => {
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  now.setDate(now.getDate() + offsetDays);
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatIstDayLabel = (value) => {
  if (!value) return "";
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00+05:30` : value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const fallbackRevenueWindow = (days = 30) => {
  const windowDays = Math.max(1, Number(days) || 30);
  const startDate = dateInIst(-(windowDays - 1));
  const endDate = dateInIst(0);
  return {
    days: windowDays,
    startDate,
    endDate,
    startLabel: formatIstDayLabel(startDate),
    endLabel: formatIstDayLabel(endDate),
  };
};

const revenueWindowRange = (window, days = 30) => {
  const source = (window?.startDate || window?.startLabel) ? window : fallbackRevenueWindow(days);
  const from = source.startLabel || formatIstDayLabel(source.startDate);
  const to = source.endLabel || formatIstDayLabel(source.endDate);
  return { from, to, days: source.days || days };
};

const HeadTip = ({ label, title }) => (
  <Tooltip {...instantTooltip} title={title}>
    <span style={{ cursor: "help", borderBottom: "1px dashed #999" }}>{label}</span>
  </Tooltip>
);

const CountChip = ({ label, value }) => (
  <span style={{ marginLeft: 12, fontWeight: 500 }}>
    {label}: <strong style={{ fontWeight: 600 }}>{value || 0}</strong>
  </span>
);

const CheckRow = ({ checked, onChange, children }) => (
  <label
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      margin: 0,
      cursor: "pointer",
      whiteSpace: "nowrap",
      fontSize: 14,
      fontWeight: 500,
      color: "#333",
      flex: "0 0 auto",
    }}
  >
    <input type="checkbox" checked={checked} onChange={onChange} style={{ margin: 0, width: 16, height: 16 }} />
    {children}
  </label>
);

const FilterField = ({ label, hint, minWidth, children }) => (
  <div className="form-group" style={{ minWidth, flex: "0 0 auto" }}>
    {label ? (
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#444", marginBottom: 4, whiteSpace: "nowrap" }}>
        {label}
      </label>
    ) : null}
    {children}
    {hint ? (
      <div style={{ fontSize: 11, color: "#888", marginTop: 4, lineHeight: 1.35, maxWidth: minWidth || 200 }}>
        {hint}
      </div>
    ) : null}
  </div>
);

const GuideBox = ({ title, meaning, points }) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #eee",
      borderRadius: 8,
      padding: "12px 16px",
      margin: "0 0 16px",
      color: "#444",
      fontSize: 13,
      lineHeight: 1.55,
    }}
  >
    <div style={{ fontWeight: 600, fontSize: 14, color: "#242424", marginBottom: 4 }}>{title}</div>
    <div style={{ marginBottom: 6 }}>{meaning}</div>
    <ul style={{ margin: 0, paddingLeft: 18 }}>
      {points.map((point) => (
        <li key={point}>{point}</li>
      ))}
    </ul>
  </div>
);

const DeskGuide = ({ tab, desk3Kind, settings, minSpend, audienceTotal, revenueWindow }) => {
  const months = settings.powerWindowMonths || 6;
  const power = Number(settings.powerSpendThreshold) || 1500;
  const platinum = Number(settings.platinumPowerSpendThreshold) || 5000;
  const potential = Number(settings.potentialPowerSpendMin) || 500;
  const top20Days = Number(settings.top20WindowDays) || 30;
  const caseBSpend = Number(minSpend) || Number(settings.caseBMinSpend) || 250;
  const money = (value) => `₹${Number(value).toLocaleString("en-IN")}`;
  const { from, to } = revenueWindowRange(revenueWindow, top20Days);
  const revenueRangeText = from && to ? ` from ${from} to ${to}` : "";

  if (tab === "desk1") {
    return (
      <GuideBox
        title="Desk 1 — Priority consultants"
        meaning="Operations person 1 manages only high-priority consultants: Platinum, Gold, and Top 20."
        points={[
          "Platinum and Gold are set from the Tier dropdown on each row. If a consultant already has a tag named Platinum or Gold, they also come into this desk.",
          `Top 20 is ranked by last ${top20Days} days (${from} to ${to}, IST) Consultant Payout earning — same wallet credits as payout Chat + Call + Gift + Session.`,
          `${top20Days}d revenue${revenueRangeText} matches Consultant Payout for those dates: consultant wallet credits for chat, call/live, gift, and session booking. Highest revenue is shown first.`,
        ]}
      />
    );
  }
  if (tab === "desk2") {
    return (
      <GuideBox
        title="Desk 2 — Power customers"
        meaning="Operations person 2 manages high-spend customers: Power and Platinum Power."
        points={[
          `Spend is the total deducted on completed private consultations (chat/call) in the last ${months} months, across all consultants.`,
          `Gift is the wallet amount this customer sent as gifts to consultants in the same ${months} months.`,
          `Power customer: spend ${money(power)} or more.`,
          `Platinum Power customer: spend ${money(platinum)} or more, or the customer has a Platinum tag.`,
          `Potential Power is not on this desk. That is spend ${money(potential)} to ${money(power - 1)}, or a previous potential-power tag.`,
        ]}
      />
    );
  }
  if (tab === "desk3") {
    return desk3Kind === "consultants" ? (
      <GuideBox
        title="Desk 3 — Remaining consultants"
        meaning="Operations person 3 manages every verified consultant who is not Platinum, Gold, or Top 20."
        points={[
          "This list is all live, verified consultants minus Desk 1.",
          "Change Tier on a row to Platinum / Gold / Top 20 to move that consultant to Desk 1.",
          `${top20Days}d revenue${revenueRangeText} matches Consultant Payout Chat + Call + Gift + Session for the same dates. Highest revenue is shown first.`,
        ]}
      />
    ) : (
      <GuideBox
        title="Desk 3 — Remaining customers"
        meaning="Operations person 3 also manages everyday customers — everyone except Power, Platinum Power, and Potential Power."
        points={[
          `A customer is excluded from this list if last ${months} months completed consult spend is ${money(potential)} or more, or they were tagged potential / platinum.`,
          "Normal / new / low-spend customers stay here, including users with no consultation yet.",
          "Spend is completed private consultation amount, not recharge amount.",
          `Gift is the wallet amount this customer sent as gifts to consultants in the last ${months} months.`,
        ]}
      />
    );
  }
  return (
    <GuideBox
      title="WhatsApp offers"
      meaning="Send a WhatsApp campaign to a customer audience. Offer dropdown is only to name the offer in the message."
      points={[
        "Case A — Returning customers: at least 2 completed private consultations, any spend.",
        `Case B — Spend in date range: completed consult spend of ${money(caseBSpend)} or more between the start and end dates you pick.`,
        "Exclude power / potential power: removes Desk 2 and Potential Power users from the audience.",
        "Send to all matching, or tick people and choose Send to selected. If nobody is selected, the campaign goes to everyone in this audience.",
        `One send covers up to 150 people. Current matching audience: ${audienceTotal || 0}.`,
      ]}
    />
  );
};

const PaginationBar = ({ total, page, limit, onPage, onLimit, hideLimit }) => (
  <div className="pagination-wrapper mt-4 d-flex justify-content-between align-items-center">
    {hideLimit ? <span /> : (
      <div className="d-flex align-items-center gap-3">
        <span>Show:</span>
        <select
          value={limit}
          onChange={(event) => onLimit(Number(event.target.value))}
          style={{ padding: "8px 12px", border: "1px solid #ccc", borderRadius: 4, minWidth: 80 }}
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
        <span>per page</span>
      </div>
    )}
    <span>
      Showing {total ? (page - 1) * limit + 1 : 0} to {Math.min(page * limit, total || 0)} of {total || 0} items
    </span>
    {total > limit ? (
      <div className="Pagination">
        <Pagination
          activePage={page}
          itemsCountPerPage={limit}
          totalItemsCount={total}
          pageRangeDisplayed={5}
          onChange={onPage}
          itemClass="page-item"
          linkClass="page-link"
        />
      </div>
    ) : null}
  </div>
);

const OperationsEcosystem = () => {
  const { rightsInfo, role } = useSelector((state) => state?.vedicRights);
  const tabAccess = useMemo(() => getOpsEcosystemTabAccess(rightsInfo, role), [rightsInfo, role]);
  const visibleTabs = useMemo(() => TABS.filter((item) => tabAccess[item.id]), [tabAccess]);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [tab, setTab] = useState(() => visibleTabs[0]?.id || "desk1");
  const [desk3Kind, setDesk3Kind] = useState("consultants");
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState({
    platinumPowerSpendThreshold: 5000,
    caseBMinSpend: 250,
    top20WindowDays: 30,
    powerSpendThreshold: 1500,
    potentialPowerSpendMin: 500,
    powerWindowMonths: 6,
  });

  const [search, setSearch] = useState("");
  const debounceValue = useDebounce(search || "", 300);
  const [tierFilter, setTierFilter] = useState("all");
  const [customerType, setCustomerType] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [revenueWindow, setRevenueWindow] = useState(null);

  const [offers, setOffers] = useState([]);
  const [caseType, setCaseType] = useState("A");
  const [startDate, setStartDate] = useState(dateInIst(-30));
  const [endDate, setEndDate] = useState(dateInIst());
  const [minSpend, setMinSpend] = useState(250);
  const [excludePower, setExcludePower] = useState(false);
  const [offerRuleId, setOfferRuleId] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [sendWhatsapp, setSendWhatsapp] = useState(true);
  const [audience, setAudience] = useState({ list: [], total: 0 });
  const [audiencePage, setAudiencePage] = useState(1);
  const [logs, setLogs] = useState({ list: [], total: 0 });
  const [logPage, setLogPage] = useState(1);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sendMode, setSendMode] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);

  const loadSummary = useCallback(async () => {
    const { data } = await fetchOpsSummaryApi();
    if (data.code === 200) {
      setSummary(data.result);
      if (data.result?.settings) {
        setSettings((prev) => ({ ...prev, ...data.result.settings }));
        setMinSpend(data.result.settings.caseBMinSpend || 250);
      }
    }
  }, []);

  const loadDesk = useCallback(async () => {
    showLoader();
    try {
      if (tab === "desk1" || (tab === "desk3" && desk3Kind === "consultants")) {
        const { data } = await fetchOpsConsultantsApi({
          desk: tab === "desk1" ? 1 : 3,
          tier: tab === "desk1" ? tierFilter : "all",
          search: debounceValue,
          page,
          limit,
        });
        if (data.code === 200) {
          setList(data.result?.list || []);
          setTotal(data.result?.total || 0);
          setRevenueWindow(data.result?.revenueWindow || null);
        } else toastMessage(data.message, "error", "opsDesk");
      } else if (tab === "desk2" || (tab === "desk3" && desk3Kind === "customers")) {
        const { data } = await fetchOpsCustomersApi({
          desk: tab === "desk2" ? 2 : 3,
          type: tab === "desk2" ? customerType : "all",
          search: debounceValue,
          page,
          limit,
        });
        if (data.code === 200) {
          setList(data.result?.list || []);
          setTotal(data.result?.total || 0);
        } else toastMessage(data.message, "error", "opsDesk");
      }
    } catch (error) {
      toastMessage(error.message || "Failed to load desk", "error", "opsDesk");
    } finally {
      hideLoader();
    }
  }, [customerType, debounceValue, desk3Kind, hideLoader, limit, page, showLoader, tab, tierFilter]);

  const loadOffersTab = useCallback(async () => {
    showLoader();
    try {
      const [offerRes, audienceRes, logRes] = await Promise.all([
        fetchOpsOffersApi(),
        fetchOpsAudienceApi({
          caseType,
          startDate: caseType === "B" ? startDate : "",
          endDate: caseType === "B" ? endDate : "",
          minSpend: caseType === "B" ? minSpend : "",
          excludePower,
          page: audiencePage,
          limit,
        }),
        fetchOpsWhatsappLogsApi({ page: logPage, limit: 10 }),
      ]);
      if (offerRes.data.code === 200) setOffers(offerRes.data.result || []);
      if (audienceRes.data.code === 200) setAudience(audienceRes.data.result || { list: [], total: 0 });
      else toastMessage(audienceRes.data.message, "error", "opsAudience");
      if (logRes.data.code === 200) setLogs(logRes.data.result || { list: [], total: 0 });
    } catch (error) {
      toastMessage(error.message || "Failed to load offers", "error", "opsOffers");
    } finally {
      hideLoader();
    }
  }, [audiencePage, caseType, endDate, excludePower, hideLoader, limit, logPage, minSpend, showLoader, startDate]);

  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.some((item) => item.id === tab)) {
      setTab(visibleTabs[0].id);
    }
  }, [tab, visibleTabs]);

  useEffect(() => {
    if (!visibleTabs.length) return;
    loadSummary();
  }, [loadSummary, visibleTabs.length]);

  useEffect(() => {
    setSelectedIds([]);
    setSendMode("all");
  }, [caseType, startDate, endDate, minSpend, excludePower]);

  useEffect(() => {
    if (!visibleTabs.some((item) => item.id === tab)) return;
    if (tab === "offers") loadOffersTab();
    else loadDesk();
  }, [loadDesk, loadOffersTab, tab, visibleTabs]);

  const resetFilters = () => {
    setSearch("");
    setTierFilter("all");
    setCustomerType("all");
    setPage(1);
    setDesk3Kind("consultants");
  };

  const changeTier = async (consultantId, opsTier) => {
    const { data } = await setOpsConsultantTierApi({ consultantId, opsTier });
    if (data.code === 200) {
      toastMessage("Tier updated", "success", "opsTier");
      loadDesk();
      loadSummary();
    } else toastMessage(data.message, "error", "opsTier");
  };

  const assignTop20 = async () => {
    showLoader();
    try {
      const { data } = await autoAssignTop20Api();
      if (data.code === 200) {
        toastMessage(`Top 20 assigned (${data.result?.assigned || 0})`, "success", "opsTop20");
        loadDesk();
        loadSummary();
      } else toastMessage(data.message, "error", "opsTop20");
    } finally {
      hideLoader();
    }
  };

  const saveSettings = async () => {
    const { data } = await saveOpsSettingsApi(settings);
    if (data.code === 200) {
      toastMessage("Settings saved", "success", "opsSettings");
      setSettings(data.result);
      loadSummary();
    } else toastMessage(data.message, "error", "opsSettings");
  };

  const sendCampaign = async () => {
    showLoader();
    try {
      const { data } = await sendOpsWhatsappApi({
        caseType,
        startDate: caseType === "B" ? startDate : undefined,
        endDate: caseType === "B" ? endDate : undefined,
        minSpend: caseType === "B" ? minSpend : undefined,
        excludePower,
        offerRuleId: offerRuleId || undefined,
        campaignName,
        sendWhatsapp,
        sendMode,
        userIds: sendMode === "selected" ? selectedIds : undefined,
      });
      if (data.code === 200) {
        const result = data.result || {};
        toastMessage(
          sendMode === "selected"
            ? `Sent to selected people: ${result.sent || 0} sent, ${result.failed || 0} failed`
            : `Processed ${result.processed || 0}: sent ${result.sent || 0}, remaining ${result.remaining || 0}`,
          "success",
          "opsSend"
        );
        setConfirmSend(false);
        setSelectedIds([]);
        loadOffersTab();
      } else toastMessage(data.message, "error", "opsSend");
    } catch (error) {
      toastMessage(error.message || "Send failed", "error", "opsSend");
    } finally {
      hideLoader();
    }
  };

  const openSendConfirm = () => {
    if (sendMode === "selected" && !selectedIds.length) {
      toastMessage("Select people from the table, or choose Send to all matching", "error", "opsSend");
      return;
    }
    setConfirmSend(true);
  };

  const audiencePageIds = (audience.list || []).map((row) => String(row._id));
  const allPageSelected = audiencePageIds.length > 0 && audiencePageIds.every((id) => selectedIds.includes(id));

  const toggleAudienceRow = (id) => {
    const key = String(id);
    const next = selectedIds.includes(key)
      ? selectedIds.filter((item) => item !== key)
      : [...selectedIds, key];
    setSelectedIds(next);
    setSendMode(next.length ? "selected" : "all");
  };

  const toggleAudiencePage = () => {
    const next = allPageSelected
      ? selectedIds.filter((id) => !audiencePageIds.includes(id))
      : Array.from(new Set([...selectedIds, ...audiencePageIds]));
    setSelectedIds(next);
    setSendMode(next.length ? "selected" : "all");
  };

  const isConsultantDesk = tab === "desk1" || (tab === "desk3" && desk3Kind === "consultants");
  const counts = summary || {};
  const { from: revenueFrom, to: revenueTo, days: revenueDays } = revenueWindowRange(
    revenueWindow,
    settings.top20WindowDays,
  );
  const revenueRangeText = revenueFrom && revenueTo ? ` from ${revenueFrom} to ${revenueTo}` : "";

  return (
    <>
      <div className="WrapperArea">
        <div className="WrapperBox">
          {loader}
          <div className="TitleBox" style={{ alignItems: "center" }}>
            <Tooltip
              {...instantTooltip}
              title="Three operations desks plus WhatsApp offers. Desk 1: Platinum, Gold, Top 20 consultants. Desk 2: Power and Platinum Power customers. Desk 3: remaining consultants and customers."
            >
              <h4 className="Title" style={{ fontWeight: 500, fontSize: 20 }}>
                Operations Ecosystem
              </h4>
            </Tooltip>
            <h5 style={{ fontWeight: 500, fontSize: 14, margin: 0 }}>
              {tabAccess.desk1 ? <CountChip label="Desk 1" value={counts.consultants?.desk1} /> : null}
              {tabAccess.desk2 ? <CountChip label="Desk 2" value={counts.customers?.desk2} /> : null}
              {tabAccess.desk3 ? <CountChip label="Desk 3 consultants" value={counts.consultants?.desk3} /> : null}
              {tabAccess.desk3 ? <CountChip label="Desk 3 customers" value={counts.customers?.desk3} /> : null}
            </h5>
          </div>

          {!visibleTabs.length ? (
            <p>You do not have permission to view this section.</p>
          ) : (
            <Box sx={{ width: "100%", mb: 2 }}>
              <CustomTabs
                value={Math.max(0, visibleTabs.findIndex((item) => item.id === tab))}
                variant="scrollable"
                allowScrollButtonsMobile
                onChange={(_, index) => {
                  setTab(visibleTabs[index].id);
                  setPage(1);
                  setList([]);
                }}
              >
                {visibleTabs.map((item) => (
                  <CustomTab key={item.id} label={item.label} />
                ))}
              </CustomTabs>
            </Box>
          )}

          {!visibleTabs.length || !tabAccess[tab] ? null : tab !== "offers" ? (
            <>
              <div className="FilterBox FilterArea">
                {tab === "desk3" ? (
                  <div className="form-group select_sm_width" style={{ minWidth: 260, flex: "0 0 auto" }}>
                    <select
                      className="form-control"
                      value={desk3Kind}
                      onChange={(event) => {
                        setDesk3Kind(event.target.value);
                        setPage(1);
                      }}
                    >
                      <option value="consultants">Consultants (minus Platinum / Gold / Top 20)</option>
                      <option value="customers">Customers (minus Power / Potential Power)</option>
                    </select>
                  </div>
                ) : null}
                {tab === "desk1" ? (
                  <div className="form-group select_sm_width" style={{ minWidth: 160, flex: "0 0 auto" }}>
                    <select
                      className="form-control"
                      value={tierFilter}
                      onChange={(event) => {
                        setTierFilter(event.target.value);
                        setPage(1);
                      }}
                    >
                      <option value="all">All Desk 1</option>
                      <option value="platinum">Platinum</option>
                      <option value="gold">Gold</option>
                      <option value="top20">Top 20</option>
                    </select>
                  </div>
                ) : null}
                {tab === "desk2" ? (
                  <div className="form-group select_sm_width" style={{ minWidth: 200, flex: "0 0 auto" }}>
                    <select
                      className="form-control"
                      value={customerType}
                      onChange={(event) => {
                        setCustomerType(event.target.value);
                        setPage(1);
                      }}
                    >
                      <option value="all">Power + Platinum Power</option>
                      <option value="power">Power</option>
                      <option value="platinum_power">Platinum Power</option>
                    </select>
                  </div>
                ) : null}
                <div className="form-group search_lg_size userSearch">
                  <input
                    type="search"
                    className={`form-control ${search && "active"}`}
                    placeholder="Search by Name, Phone, Email"
                    value={search || ""}
                    style={{ "--color": "#FF8989" }}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setPage(1);
                    }}
                  />
                  <span className="user_search_icon">
                    <IoSearch />
                  </span>
                </div>
                <div className="form-group refresh_button" style={{ minWidth: 120, flex: "0 0 auto", display: "flex", gap: 10 }}>
                  {tab === "desk1" ? (
                    <SecondaryButton onClick={assignTop20}>
                      Auto Top 20
                    </SecondaryButton>
                  ) : null}
                  <SecondaryButton onClick={resetFilters}>
                    <RotateLeftIcon />
                  </SecondaryButton>
                </div>
              </div>

              <DeskGuide tab={tab} desk3Kind={desk3Kind} settings={settings} revenueWindow={revenueWindow} />

              <div className="content-table">
                <table>
                  <thead>
                    <tr>
                      <th>S. No.</th>
                      <th>Profile</th>
                      <th>Name</th>
                      <th>Phone</th>
                      {isConsultantDesk ? (
                        <>
                          <th><HeadTip label="Live" title="Current chat/call/video live toggles." /></th>
                          <th><HeadTip label="Tier" title="Official operations tier. Desk 1 = Platinum, Gold, Top 20." /></th>
                          <th>
                            <HeadTip
                              label={`${revenueDays}d revenue`}
                              title={`Same as Consultant Payout earning${revenueRangeText} (IST). Chat + Call + Gift + Session wallet credits. Sorted highest first.`}
                            />
                            {revenueFrom && revenueTo ? (
                              <div style={{ fontSize: 11, fontWeight: 400, color: "#666", marginTop: 2, whiteSpace: "nowrap" }}>
                                {revenueFrom} – {revenueTo}
                              </div>
                            ) : null}
                          </th>
                          <th>Completed</th>
                        </>
                      ) : (
                        <>
                          <th>Type</th>
                          <th><HeadTip label="Spend" title="Completed consult spend in the current 6-month power window." /></th>
                          <th><HeadTip label="Gift" title="Gift amount this customer sent to consultants in the same 6-month window." /></th>
                          <th>Completed</th>
                        </>
                      )}
                      <th>Last consult</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.length ? list.map((row, index) => (
                      <tr key={row._id}>
                        <td>{findSerialNumber(index, page, limit)}</td>
                        <td className="user_profile_image">
                          <img
                            src={row.profileImage || profileImage}
                            alt=""
                            onError={(event) => { event.currentTarget.src = profileImage; }}
                          />
                        </td>
                        <td>
                          <Link
                            to={isConsultantDesk
                              ? `${consultant.CONSULTANT_DETAIL}/${row._id}?from=operations`
                              : `${USER_DETAIL}/${row._id}`}
                          >
                            {row.name ? CapitalizeFirstWord(row.name) : "---"}
                          </Link>
                        </td>
                        <td>
                          {row.phonePrefix ? `+${row.phonePrefix}` : ""}
                          {row.phone || "---"}
                        </td>
                        {isConsultantDesk ? (
                          <>
                            <td>{row.liveLabel || (row.isOnline ? "Online" : "Offline")}</td>
                            <td>
                              <select
                                className="form-control"
                                value={row.opsTier || ""}
                                onChange={(event) => changeTier(row._id, event.target.value)}
                                style={{ minWidth: 120 }}
                              >
                                {TIER_OPTIONS.map((option) => (
                                  <option key={option.value || "none"} value={option.value}>{option.label}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <HeadTip
                                label={formatMoney(row.windowRevenue)}
                                title={`${row.windowDays || revenueDays}d revenue${row.windowStartLabel && row.windowEndLabel ? ` from ${row.windowStartLabel} to ${row.windowEndLabel}` : revenueRangeText} (IST). Same as Consultant Payout: Chat ${formatMoney(row.windowChatRevenue)} + Call ${formatMoney(row.windowCallRevenue)} + Gift ${formatMoney(row.windowGiftRevenue)} + Session ${formatMoney(row.windowSessionRevenue)}.`}
                              />
                            </td>
                            <td>{row.windowCompleted ?? 0}</td>
                          </>
                        ) : (
                          <>
                            <td>{row.customerTypeLabel || "---"}</td>
                            <td>{formatMoney(row.spend)}</td>
                            <td>{formatMoney(row.giftSpend)}</td>
                            <td>{row.completed ?? 0}</td>
                          </>
                        )}
                        <td>{formatDate(row.lastConsultAt)}</td>
                        <td>
                          <div className="action_button">
                            <Link
                              to={isConsultantDesk
                                ? `${consultant.CONSULTANT_DETAIL}/${row._id}?from=operations`
                                : `${USER_DETAIL}/${row._id}`}
                            >
                              <IconButton aria-label="details" className="px-2 py-0">
                                <VisibilityIcon className="eye_icon" />
                              </IconButton>
                            </Link>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <NotFound cols={11} msg="No records found" icon={NOT_FOUND} loader={null} />
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationBar
                total={total}
                page={page}
                limit={limit}
                onPage={setPage}
                onLimit={(value) => { setLimit(value); setPage(1); }}
              />
            </>
          ) : (
            <>
              <div className="FilterBox FilterArea" style={{ flexWrap: "wrap", alignItems: "center" }}>
                <div className="form-group select_sm_width" style={{ minWidth: 220, flex: "0 0 auto" }}>
                  <select className="form-control" value={caseType} onChange={(event) => { setCaseType(event.target.value); setAudiencePage(1); }}>
                    <option value="A">Case A - Returning customers</option>
                    <option value="B">Case B - Spend in date range</option>
                  </select>
                </div>
                {caseType === "B" ? (
                  <>
                    <div className="form-group" style={{ minWidth: 150, flex: "0 0 auto" }}>
                      <input type="date" className="form-control" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
                    </div>
                    <div className="form-group" style={{ minWidth: 150, flex: "0 0 auto" }}>
                      <input type="date" className="form-control" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
                    </div>
                    <div className="form-group" style={{ minWidth: 130, flex: "0 0 auto" }}>
                      <input type="number" min={1} className="form-control" value={minSpend} onChange={(event) => setMinSpend(event.target.value)} placeholder="Min spend" />
                    </div>
                  </>
                ) : null}
                <div className="form-group select_sm_width" style={{ minWidth: 200, flex: "0 0 auto" }}>
                  <select className="form-control" value={offerRuleId} onChange={(event) => setOfferRuleId(event.target.value)}>
                    <option value="">Select offer rule</option>
                    {offers.map((offer) => (
                      <option key={offer._id} value={offer._id}>{offer.name}</option>
                    ))}
                  </select>
                </div>
                <FilterField label="AiSensy campaign name" hint="WhatsApp campaign name as created in AiSensy." minWidth={220}>
                  <input
                    type="text"
                    className="form-control"
                    value={campaignName}
                    onChange={(event) => setCampaignName(event.target.value)}
                    placeholder="e.g. returning_offer_oct"
                  />
                </FilterField>
                <FilterField
                  label="Platinum Power spend (₹)"
                  hint="Completed consult spend to mark a customer as Platinum Power. Saved with Save settings."
                  minWidth={200}
                >
                  <input
                    type="number"
                    min={1}
                    className="form-control"
                    value={settings.platinumPowerSpendThreshold}
                    onChange={(event) => setSettings((prev) => ({ ...prev, platinumPowerSpendThreshold: event.target.value }))}
                    placeholder="e.g. 5000"
                    title="Platinum Power spend threshold in rupees"
                  />
                </FilterField>
                <FilterField
                  label="Top 20 window (days)"
                  hint="Last N days used to rank Top 20 consultants by payout revenue. Saved with Save settings."
                  minWidth={190}
                >
                  <input
                    type="number"
                    min={1}
                    className="form-control"
                    value={settings.top20WindowDays}
                    onChange={(event) => setSettings((prev) => ({ ...prev, top20WindowDays: event.target.value }))}
                    placeholder="e.g. 30"
                    title="Days used for Top 20 consultant ranking"
                  />
                </FilterField>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 18,
                    flex: "0 0 auto",
                    flexWrap: "wrap",
                  }}
                >
                  <CheckRow checked={excludePower} onChange={(event) => setExcludePower(event.target.checked)}>
                    Exclude power / potential power
                  </CheckRow>
                  <CheckRow checked={sendWhatsapp} onChange={(event) => setSendWhatsapp(event.target.checked)}>
                    Send WhatsApp
                  </CheckRow>
                </div>
                <div className="form-group select_sm_width" style={{ minWidth: 220, flex: "0 0 auto" }}>
                  <select
                    className="form-control"
                    value={sendMode}
                    onChange={(event) => setSendMode(event.target.value)}
                  >
                    <option value="all">Send to all matching</option>
                    <option value="selected">Send to selected people</option>
                  </select>
                </div>
                {selectedIds.length ? (
                  <span style={{ fontSize: 13, fontWeight: 500, color: "#444", whiteSpace: "nowrap" }}>
                    {selectedIds.length} selected
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedIds([]);
                        setSendMode("all");
                      }}
                      style={{
                        marginLeft: 8,
                        border: "none",
                        background: "none",
                        color: "#c44",
                        cursor: "pointer",
                        padding: 0,
                        fontSize: 13,
                      }}
                    >
                      Clear
                    </button>
                  </span>
                ) : null}
                <div className="form-group refresh_button" style={{ minWidth: 120, flex: "0 0 auto", display: "flex", gap: 10 }}>
                  <SecondaryButton onClick={saveSettings}>Save settings</SecondaryButton>
                  <SecondaryButton onClick={loadOffersTab}>Preview audience</SecondaryButton>
                  <SecondaryButton onClick={openSendConfirm}>Send campaign</SecondaryButton>
                </div>
              </div>

              <DeskGuide
                tab="offers"
                desk3Kind={desk3Kind}
                settings={settings}
                minSpend={minSpend}
                audienceTotal={audience.total}
              />

              <div className="content-table">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 42 }}>
                        <input
                          type="checkbox"
                          checked={allPageSelected}
                          onChange={toggleAudiencePage}
                          disabled={!audiencePageIds.length}
                          title="Select all on this page"
                          style={{ margin: 0, width: 16, height: 16 }}
                        />
                      </th>
                      <th>S. No.</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Type</th>
                      <th>Spend</th>
                      <th>Gift</th>
                      <th>Completed</th>
                      <th>Last consult</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audience.list?.length ? audience.list.map((row, index) => (
                      <tr key={row._id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(String(row._id))}
                            onChange={() => toggleAudienceRow(row._id)}
                            style={{ margin: 0, width: 16, height: 16 }}
                          />
                        </td>
                        <td>{findSerialNumber(index, audiencePage, limit)}</td>
                        <td>
                          <Link to={`${USER_DETAIL}/${row._id}`}>
                            {row.name ? CapitalizeFirstWord(row.name) : "---"}
                          </Link>
                        </td>
                        <td>{row.phonePrefix ? `+${row.phonePrefix}` : ""}{row.phone || "---"}</td>
                        <td>{row.customerTypeLabel || "---"}</td>
                        <td>{formatMoney(row.spend)}</td>
                        <td>{formatMoney(row.giftSpend)}</td>
                        <td>{row.completed ?? 0}</td>
                        <td>{formatDate(row.lastConsultAt)}</td>
                        <td>
                          <Link to={`${USER_DETAIL}/${row._id}`}>
                            <IconButton aria-label="details" className="px-2 py-0">
                              <VisibilityIcon className="eye_icon" />
                            </IconButton>
                          </Link>
                        </td>
                      </tr>
                    )) : (
                      <NotFound cols={10} msg="No audience found for this case" icon={NOT_FOUND} loader={null} />
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationBar
                total={audience.total || 0}
                page={audiencePage}
                limit={limit}
                onPage={setAudiencePage}
                onLimit={(value) => { setLimit(value); setAudiencePage(1); }}
              />

              {/* <h5 style={{ marginTop: 28 }}>Recent campaigns</h5>
              <div className="content-table">
                <table>
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Case</th>
                      <th>Offer</th>
                      <th>Campaign</th>
                      <th>Target</th>
                      <th>Audience</th>
                      <th>Sent</th>
                      <th>Failed</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.list?.length ? logs.list.map((row) => (
                      <tr key={row._id}>
                        <td>{formatDate(row.createdAt)}</td>
                        <td>{row.caseType}</td>
                        <td>{row.offerName || "---"}</td>
                        <td>{row.campaignName || "---"}</td>
                        <td>{row.sendMode === "selected" ? "Selected people" : "All matching"}</td>
                        <td>{row.totalAudience ?? 0}</td>
                        <td>{row.sent ?? 0}</td>
                        <td>{row.failed ?? 0}</td>
                        <td>{row.status || "---"}</td>
                      </tr>
                    )) : (
                      <NotFound cols={9} msg="No campaigns yet" icon={NOT_FOUND} loader={null} />
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationBar
                total={logs.total || 0}
                page={logPage}
                limit={10}
                onPage={setLogPage}
                hideLimit
              /> */}
            </>
          )}
        </div>
      </div>

      {confirmSend ? (
        <ModalBox
          status={confirmSend}
          onClose={() => setConfirmSend(false)}
          title="Send WhatsApp offer?"
          closeIcon
          noFooter
        >
          <p>
            {sendMode === "selected" ? (
              <>
                This campaign will go only to <strong>{selectedIds.length}</strong> selected{" "}
                {selectedIds.length === 1 ? "person" : "people"}
                {selectedIds.length > 150 ? " (this run sends up to 150)." : "."}
              </>
            ) : (
              <>
                Case {caseType}: <strong>{audience.total || 0}</strong> people match.
                Campaign will go to all matching people (up to 150).
              </>
            )}
          </p>
          <p>Offer: <strong>{offers.find((item) => item._id === offerRuleId)?.name || "None selected"}</strong></p>
          <p>Campaign: <strong>{campaignName || "—"}</strong></p>
          <p>{sendWhatsapp ? "WhatsApp will be sent." : "WhatsApp will not be sent."}</p>
          <div className="d-flex justify-content-end" style={{ gap: 8, marginTop: 16 }}>
            <SecondaryButton onClick={() => setConfirmSend(false)}>Cancel</SecondaryButton>
            <SecondaryButton onClick={sendCampaign}>Send now</SecondaryButton>
          </div>
        </ModalBox>
      ) : null}
    </>
  );
};

export default OperationsEcosystem;
